import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchStoreAuditWorksheet,
  fetchStoreStocks,
  saveStoreAuditWorksheet,
  type StoreAuditWorksheetDto,
  type StoreStockRow,
} from "../api/mrrInventoryApi";
import { PageHeader } from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { useAppState } from "../context/AppStateContext";

function rowKey(r: StoreStockRow): string {
  return `${r.storeYardId}\u0001${r.catalogStandardItemId}`;
}

function parseQty(raw: string): number | null {
  const t = raw.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function escapeCsvCell(v: string): string {
  if (/[",\r\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

export function StoreAuditPage() {
  const { showToast } = useToast();
  const { user, authRequired } = useAuth();
  const { storeYards } = useAppState();
  const sortedYards = useMemo(() => [...storeYards].sort((a, b) => a.sortOrder - b.sortOrder), [storeYards]);
  const [filterYardId, setFilterYardId] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [rows, setRows] = useState<StoreStockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [physicalByRow, setPhysicalByRow] = useState<Record<string, string>>({});
  const [remarkByRow, setRemarkByRow] = useState<Record<string, string>>({});
  const [reportStamp, setReportStamp] = useState(() => new Date().toLocaleString());
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authRequired || user?.role === "superadmin") return;
    const ids = user?.inventoryStoreYardIds;
    if (Array.isArray(ids) && ids.length >= 1 && filterYardId === "") {
      setFilterYardId(ids[0]);
    }
  }, [authRequired, user?.role, user?.inventoryStoreYardIds, filterYardId]);

  const filteredRows = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.resourceItemLabel ?? "").toLowerCase();
      const idStr = String(r.catalogStandardItemId);
      return name.includes(q) || idStr.includes(q);
    });
  }, [rows, itemSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchStoreStocks(filterYardId.trim() || undefined);
      let worksheet: StoreAuditWorksheetDto = {
        physicalByRow: {},
        remarkByRow: {},
        savedAt: null,
      };
      try {
        worksheet = await fetchStoreAuditWorksheet(filterYardId.trim() || undefined);
      } catch {
        /* worksheet API optional until server upgraded */
      }
      const validKeys = new Set(list.map(rowKey));
      const phy: Record<string, string> = {};
      const rem: Record<string, string> = {};
      for (const [k, v] of Object.entries(worksheet.physicalByRow)) {
        if (validKeys.has(k) && typeof v === "string") phy[k] = v;
      }
      for (const [k, v] of Object.entries(worksheet.remarkByRow)) {
        if (validKeys.has(k) && typeof v === "string") rem[k] = v;
      }
      setRows(list);
      setPhysicalByRow(phy);
      setRemarkByRow(rem);
      setLastSavedAt(worksheet.savedAt);
      setReportStamp(new Date().toLocaleString());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
      setPhysicalByRow({});
      setRemarkByRow({});
      setLastSavedAt(null);
    } finally {
      setLoading(false);
    }
  }, [filterYardId]);

  const saveWorksheet = useCallback(async () => {
    if (rows.length === 0) return;
    const allowed = new Set(rows.map(rowKey));
    const phy = Object.fromEntries(
      Object.entries(physicalByRow).filter(([k]) => allowed.has(k)),
    ) as Record<string, string>;
    const rem = Object.fromEntries(
      Object.entries(remarkByRow).filter(([k]) => allowed.has(k)),
    ) as Record<string, string>;
    setSaving(true);
    try {
      const out = await saveStoreAuditWorksheet({
        storeYardId: filterYardId.trim() || undefined,
        physicalByRow: phy,
        remarkByRow: rem,
      });
      setLastSavedAt(out.savedAt);
      showToast("Store audit worksheet saved.", "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not save worksheet.", "error");
    } finally {
      setSaving(false);
    }
  }, [rows, physicalByRow, remarkByRow, filterYardId, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const restrictedInvYards =
    authRequired && user?.role !== "superadmin" ? user?.inventoryStoreYardIds : null;
  const hideAllStoresAuditOption = Array.isArray(restrictedInvYards) && restrictedInvYards.length >= 1;

  const filterLabel =
    filterYardId.trim() === ""
      ? "All stores / yards"
      : sortedYards.find((y) => y.id === filterYardId)?.name ?? filterYardId;

  const downloadCsv = () => {
    const header = [
      "Sl No",
      "Store / yard",
      "Item",
      "Unit",
      "System qty",
      "Physical qty",
      "Variance",
      "Remark",
    ];
    const lines = filteredRows.map((r, i) => {
      const k = rowKey(r);
      const physicalRaw = physicalByRow[k] ?? "";
      const physicalNum = parseQty(physicalRaw);
      const variance =
        physicalNum === null ? "" : String(Math.round((physicalNum - r.currentStock) * 1000) / 1000);
      return [
        String(i + 1),
        r.storeYardName || r.storeYardId,
        r.resourceItemLabel || "",
        r.resourceItemUnit || "",
        String(r.currentStock),
        physicalRaw.trim(),
        variance,
        remarkByRow[k] ?? "",
      ]
        .map((c) => escapeCsvCell(String(c)))
        .join(",");
    });
    const titleLine = escapeCsvCell(`Store audit — ${filterLabel} — ${new Date().toISOString().slice(0, 10)}`);
    const csv = `\uFEFF${titleLine}\n${header.map((h) => escapeCsvCell(h)).join(",")}\n${lines.join("\n")}`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `store-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearCounts = () => {
    setPhysicalByRow({});
    setRemarkByRow({});
  };

  return (
    <div className="store-audit-page-root">
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: "Store audit" },
        ]}
        title="Store audit worksheet"
      />

      <div className="card card--gov card--elevated store-audit-page">
        <p className="section-lead section-lead--tight text-muted store-audit-no-print" style={{ marginTop: 0 }}>
          System quantities match{" "}
          <Link to="/inventory/store-stocks">Store stocks</Link>. Enter counted physical quantities during the audit;
          variance updates automatically. Use <strong>Save worksheet</strong> to store counts and remarks on the server
          (one draft per scope: all yards or the yard you selected). Export CSV or print when ready.{" "}
          <Link to="/inventory/goods-receipts">Goods receipts</Link>
          {" · "}
          <Link to="/inventory/inward">Inward</Link>
        </p>

        <div className="goods-receipts-filters store-audit-no-print" style={{ marginBottom: "1rem" }}>
          <div className="goods-receipts-filters__grid">
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Store / yard</span>
              <select
                className="form-control"
                value={filterYardId}
                onChange={(e) => setFilterYardId(e.target.value)}
              >
                {!hideAllStoresAuditOption ? <option value="">All stores / yards</option> : null}
                {sortedYards.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Search item</span>
              <input
                type="search"
                className="form-control"
                placeholder="Name or item id"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
          <div className="store-audit-toolbar">
            <button type="button" className="btn btn--tg-secondary btn--sm" onClick={() => void load()}>
              Refresh list
            </button>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              title="Clears entries on this screen; choose Save worksheet to update the saved draft on the server."
              onClick={clearCounts}
            >
              Clear physical & remarks
            </button>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              onClick={downloadCsv}
              disabled={filteredRows.length === 0}
            >
              Download CSV report
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={loading || rows.length === 0 || saving}
              onClick={() => void saveWorksheet()}
            >
              {saving ? "Saving…" : "Save worksheet"}
            </button>
            <button
              type="button"
              className="btn btn--tg-secondary btn--sm"
              onClick={() => {
                setReportStamp(new Date().toLocaleString());
                requestAnimationFrame(() => window.print());
              }}
            >
              Print report
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
            {error}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-muted">No stock rows yet (record goods receipts from Inward first).</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-muted">No items match your search.</p>
        ) : (
          <div className="store-audit-report-wrap" style={{ overflowX: "auto" }}>
            <div className="store-audit-report-head">
              <h2 className="store-audit-report-title">Store audit report</h2>
              <p className="store-audit-report-meta text-muted">
                Scope: <strong>{filterLabel}</strong>
                {" · "}
                Report generated: {reportStamp}
                {lastSavedAt ? (
                  <>
                    {" · "}
                    Last saved: {new Date(lastSavedAt).toLocaleString()}
                  </>
                ) : null}
                {" · "}
                Lines: {filteredRows.length}
              </p>
            </div>
            <table className="data-table store-audit-table">
              <thead>
                <tr>
                  <th className="store-audit-col-sl">#</th>
                  <th>Store / yard</th>
                  <th>Item</th>
                  <th>Unit</th>
                  <th>System qty</th>
                  <th>Physical qty</th>
                  <th>Variance</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, idx) => {
                  const k = rowKey(r);
                  const physicalRaw = physicalByRow[k] ?? "";
                  const physicalNum = parseQty(physicalRaw);
                  const variance =
                    physicalNum === null ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <span
                        className={
                          physicalNum === r.currentStock
                            ? ""
                            : physicalNum > r.currentStock
                              ? "store-audit-variance--pos"
                              : "store-audit-variance--neg"
                        }
                      >
                        {Math.round((physicalNum - r.currentStock) * 1000) / 1000}
                      </span>
                    );
                  return (
                    <tr key={k}>
                      <td className="mono">{idx + 1}</td>
                      <td>{r.storeYardName || r.storeYardId}</td>
                      <td>{r.resourceItemLabel || "—"}</td>
                      <td>{r.resourceItemUnit || "—"}</td>
                      <td className="mono">
                        <strong>{r.currentStock}</strong>
                      </td>
                      <td className="store-audit-physical-cell">
                        <input
                          type="text"
                          className="form-control store-audit-qty-input"
                          inputMode="decimal"
                          aria-label={`Physical quantity for ${r.resourceItemLabel || r.catalogStandardItemId}`}
                          placeholder="—"
                          value={physicalRaw}
                          onChange={(e) =>
                            setPhysicalByRow((prev) => ({
                              ...prev,
                              [k]: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td className="mono">{variance}</td>
                      <td className="store-audit-remark-cell">
                        <input
                          type="text"
                          className="form-control store-audit-remark-input"
                          placeholder="—"
                          value={remarkByRow[k] ?? ""}
                          onChange={(e) =>
                            setRemarkByRow((prev) => ({
                              ...prev,
                              [k]: e.target.value,
                            }))
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
